import * as React from 'react';
import { ApiError } from '@/domains/api';
import { workPagesApi, type IWorkPageLayout } from '../../api/work-pages-api';
import { useAdminData } from '../../components/admin-page';
import { ddWhen } from '../../dd';
import { refusalMessage } from '../../refusal';
import { useSharedWorkColumns, type IColumnChoice, type IWorkColumns } from '../lists/work-page/columns';
import type { IWorkPage } from '../lists/work-page/use-work-page';

/** Why the Columns sheet is off for this viewer — the page's `NotPermitted`, or several surveys'. */
export const COLUMNS_REASON = 'dd-columns-not-permitted';
export const COLUMNS_MULTI_REASON = 'dd-columns-several-surveys';
/** The sheet waits for the catalogue (its hover says so; no sentence on the page names it). */
const COLUMNS_READING = 'dd-columns-reading';
/** Why several surveys change no columns: each survey keeps its own layout. */
export const COLUMNS_MULTI_WORDS = 'Choose one survey to change its columns: each survey keeps its own.';

/**
 * DD calls' columns, the team's (SR-500): one layout per survey, kept by survey-api, drawn for
 * everyone; the Data team and Administrators (`work_pages.columns`) change it in the Columns
 * sheet — a draft, *Save for everyone* at the revision last read, or *Reset to default*. Several
 * surveys draw the first one's layout with the others' added fields after it, and change nothing:
 * a change belongs to one survey's layout. A save refused as stale reads the layouts again and
 * says so in the sheet.
 */
export function useDdColumns(
  work: IWorkPage,
  fixed: readonly string[],
  surveys: readonly string[],
  canChange: boolean,
  /** The id of the page's sentence saying what the roles lack, columns among it. */
  reasonId: string,
  /** The catalogue is read: before, the sheet offers none of the fields a layout holds, and a save
   *  from it would drop them for everyone. */
  ready: boolean
): IWorkColumns & { addedBy: (code: string) => string[] } {
  const read = useAdminData((signal) => workPagesApi.layouts('dd_calls', signal), []);
  // Saved or reset here since the read: what draws until the next read lands (null: reset).
  const [written, setWritten] = React.useState<ReadonlyMap<string, Nullable<IWorkPageLayout>>>(new Map());

  React.useEffect(() => setWritten(new Map()), [read.data]);
  const layoutOf = (code: string) =>
    written.has(code) ? written.get(code)! : (read.data?.find((layout) => layout.survey === code) ?? null);

  const multi = surveys.length > 1;
  const laid = surveys.map(layoutOf).filter((layout): layout is IWorkPageLayout => layout !== null);
  let choice: Nullable<IColumnChoice> = null;
  if (laid.length > 0) {
    const [first, ...rest] = laid;
    const order = [...first.order];
    for (const other of rest)
      for (const code of other.order) if (!other.known.includes(code) && !order.includes(code)) order.push(code);
    choice = { order, known: first.known };
  }

  const survey = surveys[0];
  const own = layoutOf(survey);
  /** A write's refusal in words — a stale revision read again, so the sheet shows what is saved. */
  const refusal = (error: unknown, fallback: string) => {
    if (error instanceof ApiError && error.status === 409) {
      void read.reload();
      return 'A colleague changed these columns meanwhile — they are shown as now saved. Make your change again.';
    }
    return refusalMessage(error, fallback);
  };
  const save = async (made: IColumnChoice): Promise<Nullable<string>> => {
    try {
      const { body } = await workPagesApi.save('dd_calls', survey, own?.revision ?? 0, made);
      setWritten((now) => new Map(now).set(survey, body));
      return null;
    } catch (error) {
      return refusal(error, 'The columns could not be saved.');
    }
  };
  const reset = async (): Promise<Nullable<string>> => {
    try {
      await workPagesApi.reset('dd_calls', survey, own?.revision ?? 0);
      setWritten((now) => new Map(now).set(survey, null));
      return null;
    } catch (error) {
      return refusal(error, 'The columns could not be reset.');
    }
  };

  const description = multi
    ? ''
    : own
      ? `Everyone on the team sees these columns. Last changed${own.updatedBy?.email ? ` by ${own.updatedBy.email}` : ''} ${ddWhen(own.updatedAt)}.`
      : 'Everyone on the team sees these columns.';
  const columns = useSharedWorkColumns(work, fixed, {
    choice,
    save: canChange && !multi && ready ? save : undefined,
    reset: own ? reset : undefined,
    lockedBy: !canChange ? reasonId : multi ? COLUMNS_MULTI_REASON : COLUMNS_READING,
    lockedReason: !canChange
      ? 'Your Team Portal roles do not include changing the DD calls columns.'
      : multi
        ? COLUMNS_MULTI_WORDS
        : 'The fields are still being read.',
    description
  });
  /** The chosen surveys whose layout added `code`. */
  const addedBy = (code: string) => laid.filter((layout) => layout.order.includes(code)).map((layout) => layout.survey);
  return { ...columns, addedBy };
}
